/**
 * The list behind the CRM-wide overdue-invoice banner — and the fast way to
 * clear it.
 *
 * Opened by clicking the banner (`overdue-invoices-banner`). Every overdue
 * invoice the actor may see, oldest debt first, each settleable in ONE click:
 * "Mark paid" posts `mark_as_paid` with today's date, no confirmation step and
 * no second modal. Two decisions make that safe to do without a confirm:
 *
 *  - A settled row does NOT disappear. It greys out in place with a "Paid" chip
 *    and an inline **Undo** — so rapid clicking never causes the list to
 *    reflow under the pointer and hit the wrong invoice, and a mis-click is one
 *    click to reverse (`mark_as_unpaid`).
 *  - Nothing is destroyed. Payment status is a reversible field, so the
 *    `<ConfirmDialog>` rule (destructive actions only) does not apply here.
 *
 * Bulk: tick rows (or the header box) and settle the lot with one button — the
 * "we just got paid for all of these" case.
 *
 * Money: this dialog is only ever reachable from the banner, which the layout
 * renders only for a viewer holding `financials:access` — but every amount
 * still renders through `<Money>` so the wall holds even if a future caller
 * mounts it elsewhere. See components/money.tsx.
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import { Link as RouterLink } from "react-router-dom"
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { AlertTriangle, CheckCircle2, ExternalLink, Undo2, X } from "lucide-react"

import { EmptyState } from "@/components/ui/empty-state"
import { Money } from "@crm-src/components/money"
import { useToast } from "@crm-src/hooks/use-toast"
import { formatDate } from "@crm-src/lib/date-utils"
import {
  amountOutstanding,
  daysOverdue,
  notifyOverdueInvoicesChanged,
  overdueCustomerLabel,
  type OverdueInvoice,
  type OverdueSummary,
} from "@crm-src/lib/overdue-invoices-api"

export interface OverdueInvoicesDialogProps {
  open: boolean
  onClose: () => void
  invoices: OverdueInvoice[]
  summary: OverdueSummary
  loading?: boolean
  /** Settle one invoice (today's date). Throws on failure. */
  onMarkPaid: (id: number) => Promise<void>
  /** Reverse a settle. Throws on failure. */
  onMarkUnpaid: (id: number) => Promise<void>
}

/**
 * The rows the dialog shows, given what it showed last (`previous`), the live
 * feed (`live`) and the invoices settled in this dialog session (`keepIds`).
 *
 * The feed (`useOverdueInvoices`) drops a settled invoice the moment its
 * payment lands, so the banner's count falls at once. Rendering the feed
 * directly made the settled row vanish and the rows below it jump up under
 * the pointer — the exact thing the module comment promises cannot happen.
 * So: a row already on screen keeps its place, refreshed from the feed while
 * the feed still has it, and kept as it was if it was settled (or is being
 * settled) here. Any other
 * row the feed no longer has is gone (someone else settled it). Rows new to
 * the feed go at the end, never in between.
 */
export function mergeOverdueRows(
  previous: OverdueInvoice[],
  live: OverdueInvoice[],
  keepIds: number[],
): OverdueInvoice[] {
  const liveById = new Map(live.map((invoice) => [invoice.id, invoice]))
  const kept = previous.flatMap((invoice) => {
    const current = liveById.get(invoice.id)
    if (current) return [current]
    return keepIds.includes(invoice.id) ? [invoice] : []
  })
  const shown = new Set(kept.map((invoice) => invoice.id))
  return [...kept, ...live.filter((invoice) => !shown.has(invoice.id))]
}

/** "3 days late" / "1 day late" — the phrase used in the chip and the banner. */
export function daysLateLabel(days: number): string {
  if (days <= 0) return "Due today"
  return `${days} day${days === 1 ? "" : "s"} late`
}

export function OverdueInvoicesDialog({
  open,
  onClose,
  invoices,
  summary,
  loading = false,
  onMarkPaid,
  onMarkUnpaid,
}: OverdueInvoicesDialogProps) {
  const { toast } = useToast()
  // Rows settled during THIS dialog session. They stay rendered (greyed, with
  // Undo) instead of vanishing — see the module comment.
  const [paidIds, setPaidIds] = useState<number[]>([])
  const [busyIds, setBusyIds] = useState<number[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [dirty, setDirty] = useState(false)

  // What is on screen — see mergeOverdueRows for why it is not just `invoices`.
  // A row mid-settle is kept too: the feed drops it as soon as the payment
  // lands, which is BEFORE `paidIds` records it.
  const [rows, setRows] = useState<OverdueInvoice[]>(invoices)
  useEffect(() => {
    setRows((previous) => mergeOverdueRows(previous, invoices, [...paidIds, ...busyIds]))
  }, [invoices, paidIds, busyIds])

  const isPaid = useCallback((id: number) => paidIds.includes(id), [paidIds])
  const isBusy = useCallback((id: number) => busyIds.includes(id), [busyIds])

  const withBusy = useCallback(async (id: number, run: () => Promise<void>) => {
    setBusyIds((current) => (current.includes(id) ? current : [...current, id]))
    try {
      await run()
    } finally {
      setBusyIds((current) => current.filter((busy) => busy !== id))
    }
  }, [])

  const handleMarkPaid = useCallback(
    async (invoice: OverdueInvoice) => {
      await withBusy(invoice.id, async () => {
        try {
          await onMarkPaid(invoice.id)
          setPaidIds((current) => [...current, invoice.id])
          setSelectedIds((current) => current.filter((id) => id !== invoice.id))
          setDirty(true)
        } catch {
          toast({
            title: "Couldn't mark it paid",
            description: `Invoice ${invoice.invoice_number} was not updated. Try again.`,
            severity: "error",
          })
        }
      })
    },
    [onMarkPaid, toast, withBusy],
  )

  const handleUndo = useCallback(
    async (invoice: OverdueInvoice) => {
      await withBusy(invoice.id, async () => {
        try {
          await onMarkUnpaid(invoice.id)
          setPaidIds((current) => current.filter((id) => id !== invoice.id))
          setDirty(true)
        } catch {
          toast({
            title: "Couldn't undo",
            description: `Invoice ${invoice.invoice_number} is still marked paid.`,
            severity: "error",
          })
        }
      })
    },
    [onMarkUnpaid, toast, withBusy],
  )

  const outstandingRows = useMemo(
    () => rows.filter((invoice) => !isPaid(invoice.id)),
    [rows, isPaid],
  )

  const handleMarkSelectedPaid = useCallback(async () => {
    const targets = rows.filter((invoice) => selectedIds.includes(invoice.id) && !isPaid(invoice.id))
    if (targets.length === 0) return
    // Sequential: crm-api logs a system activity per payment, and a burst of
    // parallel writes against the same tenant buys nothing here (the lists are
    // small) while making a partial failure harder to report.
    for (const invoice of targets) {
      await handleMarkPaid(invoice)
    }
  }, [handleMarkPaid, rows, isPaid, selectedIds])

  const allSelected = outstandingRows.length > 0 && selectedIds.length === outstandingRows.length
  const someSelected = selectedIds.length > 0 && !allSelected

  const toggleAll = useCallback(() => {
    setSelectedIds((current) =>
      current.length === outstandingRows.length ? [] : outstandingRows.map((invoice) => invoice.id),
    )
  }, [outstandingRows])

  const toggleOne = useCallback((id: number) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id],
    )
  }, [])

  const handleClose = useCallback(() => {
    // Tell the rest of the app (the banner, any open Invoices page) to re-read
    // only if something actually changed.
    if (dirty) notifyOverdueInvoicesChanged()
    setPaidIds([])
    setSelectedIds([])
    setDirty(false)
    onClose()
  }, [dirty, onClose])

  const remaining = outstandingRows.length
  const settled = paidIds.length

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="overdue-invoices-dialog"
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 40,
              width: 40,
              flexShrink: 0,
              borderRadius: "50%",
              bgcolor: (t) => alpha(t.palette.error.main, 0.12),
              color: "error.main",
            }}
          >
            <AlertTriangle size={20} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="div" sx={{ fontSize: "1.125rem", fontWeight: 600 }}>
              Overdue invoices
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.25 }}>
              {remaining > 0 ? (
                <>
                  {remaining} unpaid · <Money value={summary.totalAmount} options={{ decimals: 0 }} />{" "}
                  outstanding
                </>
              ) : (
                "Nothing left to chase"
              )}
              {settled > 0 ? ` · ${settled} settled just now` : ""}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        {loading && rows.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <EmptyState
              icon={<CheckCircle2 size={22} />}
              title="Nothing overdue"
              description="Every invoice you can see has been paid or is not due yet."
            />
          </Box>
        ) : (
          <>
            {selectedIds.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 2,
                  py: 1,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  {selectedIds.length} selected
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle2 size={16} />}
                  onClick={handleMarkSelectedPaid}
                  disabled={busyIds.length > 0}
                  data-testid="overdue-mark-selected-paid"
                >
                  Mark {selectedIds.length} as paid
                </Button>
              </Box>
            )}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      disabled={outstandingRows.length === 0}
                      inputProps={{ "aria-label": "Select all overdue invoices" }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Due</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Amount
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((invoice) => {
                  const paid = isPaid(invoice.id)
                  const busy = isBusy(invoice.id)
                  const late = daysOverdue(invoice)

                  return (
                    <TableRow
                      key={invoice.id}
                      hover={!paid}
                      data-testid={`overdue-invoice-row-${invoice.id}`}
                      data-paid={paid ? "true" : "false"}
                      sx={paid ? { opacity: 0.55 } : undefined}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(invoice.id)}
                          onChange={() => toggleOne(invoice.id)}
                          disabled={paid || busy}
                          inputProps={{ "aria-label": `Select invoice ${invoice.invoice_number}` }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          component={RouterLink}
                          to={`/crm/invoices/${invoice.id}`}
                          onClick={handleClose}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontWeight: 600,
                            color: "primary.main",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {invoice.invoice_number}
                          <ExternalLink size={12} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {overdueCustomerLabel(invoice)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
                            {formatDate(invoice.due_date)}
                          </Typography>
                          <Chip
                            size="small"
                            label={daysLateLabel(late)}
                            color={late >= 30 ? "error" : "warning"}
                            variant={late >= 30 ? "filled" : "outlined"}
                            sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        <Money value={amountOutstanding(invoice)} />
                      </TableCell>
                      <TableCell align="right">
                        {busy ? (
                          <CircularProgress size={18} />
                        ) : paid ? (
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <Chip
                              size="small"
                              color="success"
                              label="Paid"
                              sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                            />
                            <Tooltip title="Undo — mark unpaid again">
                              <Button
                                size="small"
                                color="inherit"
                                startIcon={<Undo2 size={14} />}
                                onClick={() => handleUndo(invoice)}
                                data-testid={`overdue-undo-${invoice.id}`}
                                sx={{ minWidth: 0 }}
                              >
                                Undo
                              </Button>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircle2 size={15} />}
                            onClick={() => handleMarkPaid(invoice)}
                            data-testid={`overdue-mark-paid-${invoice.id}`}
                          >
                            Mark paid
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 2, py: 1.5 }}>
        <Button
          component={RouterLink}
          to="/crm/invoices"
          size="small"
          color="inherit"
          onClick={handleClose}
          endIcon={<ExternalLink size={14} />}
        >
          Open the Invoices page
        </Button>
        <Button variant="contained" onClick={handleClose} data-testid="overdue-dialog-done">
          Done
        </Button>
      </Box>
    </Dialog>
  )
}

export default OverdueInvoicesDialog
